import React, { useState } from 'react';
import { projects } from '../data/mockData';
import { MapPin, Calendar, Zap, TrendingDown, Filter } from 'lucide-react';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(projects.map(p => p.category))];

  const filteredProjects = selectedCategory === 'All'
    ? projects
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Our Projects
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            5000+ successful solar installations across Karnataka and Goa
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white shadow-md top-20 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-2 flex-wrap gap-3">
            <Filter className="w-5 h-5 text-gray-600" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-[#22c55e] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-[#22c55e] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {project.category}
                  </div>
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                    {project.year}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white line-clamp-2">
                      {project.title}
                    </h3>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-gray-700 mb-6 leading-relaxed line-clamp-3">
                    {project.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 text-[#0C6951] mr-2 flex-shrink-0" />
                      <span className="font-medium">{project.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-[#0C6951] mr-2 flex-shrink-0" />
                      <span className="font-medium">Capacity: {project.capacity}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <TrendingDown className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                      <span className="font-medium">Savings: {project.savings}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-[#004534]">Client:</span> {project.client}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Stats */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Project Highlights
            </h2>
            <p className="text-xl text-gray-600">
              Our impact in numbers
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                number: '5000+',
                label: 'Projects Completed',
                description: 'Across all sectors',
              },
              {
                number: '200MW+',
                label: 'Total Capacity',
                description: 'Installed power generation',
              },
              {
                number: '₹5Cr+',
                label: 'Savings Generated',
                description: 'For our clients',
              },
              {
                number: '50,000',
                label: 'Tons CO₂ Saved',
                description: 'Environmental impact',
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-5xl font-bold text-[#004534] mb-2">
                  {stat.number}
                </div>
                <h3 className="text-xl font-semibold text-[#0C6951] mb-2">
                  {stat.label}
                </h3>
                <p className="text-sm text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Served */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Sectors We Serve
            </h2>
            <p className="text-xl text-gray-600">
              Customized solar solutions for every industry
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              'Residential',
              'Commercial',
              'Industrial',
              'Hotels',
              'Apartments',
            ].map((sector, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#004534]">{sector}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Your Solar Project Today
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Join our growing list of satisfied clients. Let's discuss your solar needs.
          </p>
          <button className="inline-flex items-center space-x-2 bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 shadow-2xl">
            <span>Request Project Consultation</span>
          </button>
        </div>
      </section>

      <style jsx>{`
        .solar-grid-bg {
          background-image: 
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Projects;
